import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import Button from '@mui/material/Button';
import {
  FileText, AlertTriangle, FileSpreadsheet, FileDown
} from 'lucide-react';
import ExportButtons from '../../components/btns/ExportButtons';
import Requests from '../../components/tables/LeaveRequests';
import LeaveDetailsModal from '../../components/DViews/LeaveDetailsModal';
import { CircularProgress, useMediaQuery, Box, Snackbar, Alert, Paper } from '@mui/material';
import ToggleHeader from '../../components/headers/ToggleHeader';
import ElegantAddButton from '../../components/btns/AddButton';
import '../../components/leaves/Leaves.css';
import {
  getPendingRequestsOlderThan15Days,
  getAllLeaves,
  getPendingRequestsStartingTomorrow,
  getPendingRequestsStartingNextWeek
} from '../../services/leavesAPIs';
import { getAllEmployees } from '../../services/EmployeeService';
import UrgentLeaveRequests from '../../components/tables/urgentRequest';
import jsPDF from 'jspdf';
import * as XLSX from 'xlsx';

// Import logos
import marabesLogo from '../../assets/images/logos/Marabes.png';
import companyLogo from '../../assets/images/logos/Axopeople.png';

const NotEmployee = localStorage.getItem('userRole') !== 'EMPLOYEE';

const VacationRequests = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const modalRef = useRef(null);
  const rejectionDialogRef = useRef(null);
  const exportContentRef = useRef(null);
  const isLargeScreen = useMediaQuery('(min-width: 1600px)');

  // =========================================================================
  // 1. STATE DEFINITIONS
  // =========================================================================
  const [IsEmployee, setIsEmployee] = useState(localStorage.getItem('userRole') === 'EMPLOYEE');

  const [selectedLeaveId, setSelectedLeaveId] = useState(null);
  const [selectedDay, setSelectedDay] = useState(null);

  const [selectedEmployeeId, setSelectedEmployeeId] = useState(null);
  const [employees, setEmployees] = useState([]);
  const [leavesData, setLeavesData] = useState([]);
  const [approved, setApproved] = useState([]);
  const [pending, setPending] = useState([]);
  const [urgentRequests, setUrgentRequests] = useState([]);
  const [startingTomorrow, setStartingTomorrow] = useState([]);
  const [startingNextWeek, setStartingNextWeek] = useState([]);
  const [hasUrgentRequests, setHasUrgentRequests] = useState(false);

  const [activeTab, setActiveTab] = useState(IsEmployee ? 'EmpRequest' : 'newOverview');

  const [filters, setFilters] = useState({
    type: '',
    status: 'pending',
    department: '',
    year: new Date().getFullYear(),
    month: new Date().getMonth() + 1,
    search: ''
  });

  const [dashboardFilters, setDashboardFilters] = useState({
    periodType: 'year',
    periodValue: new Date().getFullYear().toString(),
    office: 'All',
  });
  const [showEmployeeCard, setShowEmployeeCard] = useState(false);
  const [activeView, setActiveView] = useState('pending');
  const [isExporting, setIsExporting] = useState(false);
  const [exportError, setExportError] = useState(null);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [snackbarSeverity, setSnackbarSeverity] = useState('success');
  const [loading, setLoading] = useState(true);

  // =========================================================================
  // 2. CONFIGURATION & HELPERS
  // =========================================================================

  const leaveViews = [
    { id: 'pending', title: 'Vacation Requests' },
    { id: 'urgent', title: 'Urgent Requests' }
  ];

  const getCurrentData = () => {
    switch (activeView) {
      case 'approved': return approved;
      case 'urgent': return urgentRequests;
      case 'pending':
      default: return leavesData;
    }
  };


  const isUrgentView = activeView === 'urgent';

  const getFormattedDate = (date) => {
    if (!date) return '';
    const d = new Date(date);
    return `${d.getDate().toString().padStart(2, '0')}-${(d.getMonth() + 1).toString().padStart(2, '0')}-${d.getFullYear()}`;
  };

  // Helper to format leave type (ANNUAL_LEAVE -> Annual leave)
  const formatLeaveType = (type) => {
    if (!type) return '-';
    // Convert to string, replace underscores with space, make lowercase
    const formatted = type.toString().replace(/_/g, ' ').toLowerCase();
    // Capitalize first letter
    return formatted.charAt(0).toUpperCase() + formatted.slice(1);
  };

  const getDuration = (leave) => {
    const val = leave.duration || leave.days || leave.totalDays;
    return val ? Number(val) : 0;
  };

  const formatHours = (leave) => {
    const dur = getDuration(leave);
    if (!dur) return '-';
    return `${dur * 8}`;
  };

  // =========================================================================
  // 3. API & EFFECTS
  // =========================================================================

  const fetchData = async () => {
    setLoading(true);
    try {
      const [allLeavesData, urgentRequestsData, startingTomorrowData, startingNextWeekData, allEmployees] = await Promise.all([
        getAllLeaves(),
        getPendingRequestsOlderThan15Days(),
        getPendingRequestsStartingTomorrow(),
        getPendingRequestsStartingNextWeek(),
        getAllEmployees()
      ]);

      setLeavesData(allLeavesData);
      setUrgentRequests(urgentRequestsData);
      setStartingTomorrow(startingTomorrowData);
      setStartingNextWeek(startingNextWeekData);
      setHasUrgentRequests(
        urgentRequestsData.length > 0 ||
        startingNextWeekData.length > 0 ||
        startingTomorrowData.length > 0
      );
      setEmployees(allEmployees);
    } catch (error) {
      console.error("Error fetching ", error);
      setSnackbarMessage('Failed to fetch leave data');
      setSnackbarSeverity('error');
      setSnackbarOpen(true);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, [IsEmployee]);

  useEffect(() => {
    const employeeFilter = location.state?.employeeFilter;
    const statusFilter = location.state?.status || 'pending';
    if (employeeFilter) {
      setActiveView('pending');
      setFilters(prev => ({
        ...prev,
        search: employeeFilter,
        status: statusFilter,
        month: null,
        year: null
      }));
    }
  }, [location.state]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        selectedLeaveId &&
        modalRef.current &&
        !modalRef.current.contains(event.target) &&
        (!rejectionDialogRef.current || !rejectionDialogRef.current.contains(event.target))
      ) {
        setSelectedLeaveId(null);
      }
      if (
        selectedDay &&
        modalRef.current &&
        !modalRef.current.contains(event.target)
      ) {
        setSelectedDay(null);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [selectedLeaveId, selectedDay]);

  useEffect(() => {
    if (IsEmployee) {
      setActiveTab('EmpRequest');
    } else {
      setActiveTab('newOverview');
    }
  }, [IsEmployee]);

  // =========================================================================
  // 4. EXPORT LOGIC
  // =========================================================================

  const exportPDF = async () => {
    setIsExporting(true);
    setExportError(null);

    try {
      await new Promise(resolve => setTimeout(resolve, 500));

      const data = isUrgentView ? [...startingTomorrow, ...startingNextWeek] : getCurrentData();
      if (!data || data.length === 0) {
        throw new Error('No data available to export');
      }

      const doc = new jsPDF('p', 'mm', 'a4');
      const pageWidth = doc.internal.pageSize.width;
      const pageHeight = doc.internal.pageSize.height;
      const margin = 10;
      const contentWidth = pageWidth - (margin * 2);
      let yPos = 0;
      let currentPage = 1;

      const addPDFHeader = (pageNum) => {
        if (pageNum === 1) {
          const headerHeight = 22;
          doc.setFillColor(248, 250, 252);
          doc.rect(0, 0, pageWidth, headerHeight, 'F');

          const logoWidth = 28;
          const logoHeight = 11;
          const logoY = (headerHeight - logoHeight) / 2; // 5.5

          try {
            doc.addImage(marabesLogo, 'PNG', margin, logoY, logoWidth, logoHeight);
          } catch (e) { }

          try {
            doc.addImage(companyLogo, 'PNG', pageWidth - margin - logoWidth, logoY, logoWidth, logoHeight);
          } catch (e) { }

          doc.setFontSize(13);
          doc.setFont('helvetica', 'bold');
          doc.setTextColor(30, 41, 59);
          doc.text('VACATION REQUESTS', pageWidth / 2, 11.5, { align: 'center' });

          doc.setFontSize(8);
          doc.setFont('helvetica', 'normal');
          doc.setTextColor(100, 116, 139);
          doc.text(
            `Generated: ${new Date().toLocaleDateString('en-GB')}`,
            pageWidth / 2,
            17.5,
            { align: 'center' }
          );
        }

        /* doc.setFontSize(8);
        doc.setTextColor(100, 116, 139);
        doc.text(`Page ${pageNum}`, pageWidth - margin, pageHeight - 10, { align: 'right' }); */
      };

      const addPDFFooter = () => {
        doc.setLineWidth(0.5);
        doc.setDrawColor(226, 232, 240);
        doc.line(margin, pageHeight - 20, pageWidth - margin, pageHeight - 20);

        doc.setFontSize(7);
        doc.setTextColor(148, 163, 184);
        doc.text('Confidential - For Internal Use Only', pageWidth / 2, pageHeight - 15, { align: 'center' });

        doc.setFontSize(7);
        doc.text(`Total Records: ${data.length}`, margin, pageHeight - 10, { align: 'left' });
      };

      addPDFHeader(currentPage);
      yPos = 27;

      const tableHeaders = [
        { label: 'Employee', width: 25 },
        { label: 'Dept', width: 15 },
        { label: 'Office', width: 10 },
        { label: 'Type', width: 14 },
        { label: 'Start', width: 12 },
        { label: 'End', width: 12 },
        { label: 'Days', width: 12 },
        // { label: 'Hrs', width: 6 }
      ];

      const colPositions = [];
      let xPos = margin;
      tableHeaders.forEach(header => {
        colPositions.push(xPos);
        xPos += (header.width / 100) * contentWidth;
      });

      const addTableHeader = () => {
        doc.setFillColor(30, 41, 59);
        const headerHeight = 10;
        doc.rect(margin, yPos, contentWidth, headerHeight, 'F');

        doc.setTextColor(255, 255, 255);
        doc.setFontSize(8);
        doc.setFont('helvetica', 'bold');

        tableHeaders.forEach((header, index) => {
          doc.text(
            header.label,
            colPositions[index] + 2,
            yPos + 6,
            { align: index >= 6 ? 'center' : 'left' }
          );
        });

        yPos += headerHeight;
      };

      const addTableRow = (leave, isEven) => {
        const rowHeight = 10;

        if (isEven) {
          doc.setFillColor(248, 250, 252);
          doc.rect(margin, yPos, contentWidth, rowHeight, 'F');
        }

        doc.setTextColor(30, 41, 59);
        doc.setFontSize(7);
        doc.setFont('helvetica', 'normal');

        const durationValue = getDuration(leave);
        // Apply mapping formatting here
        const typeValue = formatLeaveType(leave.leaveType || leave.type);
        const hoursValue = formatHours(leave);

        const rowData = [
          `${leave.employee?.firstName || ''} ${leave.employee?.lastName || ''}`.trim() || 'N/A',
          leave.employee?.department?.name || '-',
          leave.employee?.office?.name || leave.employee?.location || '-',
          typeValue,
          getFormattedDate(leave.startDate),
          getFormattedDate(leave.endDate),
          durationValue ? `${durationValue}` : '-',
          // hoursValue
        ];

        rowData.forEach((cell, index) => {
          let text = cell;
          const maxWidth = (tableHeaders[index].width / 100) * contentWidth - 4;

          if (doc.getTextWidth(text) > maxWidth) {
            const splitText = doc.splitTextToSize(text, maxWidth);
            text = splitText[0];
            if (splitText.length > 1) {
              text = text.substring(0, text.length - 2) + '..';
            }
          }

          doc.text(
            text,
            colPositions[index] + 2,
            yPos + 6,
            {
              align: index >= 6 ? 'center' : 'left',
              maxWidth: maxWidth
            }
          );
        });

        yPos += rowHeight;
      };

      addTableHeader();

      data.forEach((leave, index) => {
        if (yPos + 12 > pageHeight - 20) {
          addPDFFooter();
          doc.addPage();
          currentPage++;
          addPDFHeader(currentPage);
          yPos = 20;
          addTableHeader();
        }

        addTableRow(leave, index % 2 === 0);
      });

      addPDFFooter();

      for (let i = 1; i <= currentPage; i++) {
        doc.setPage(i);
        doc.setFontSize(8);
        doc.setTextColor(100, 116, 139);
        doc.text(`Page ${i} of ${currentPage}`, pageWidth - margin, pageHeight - 10, { align: 'right' });
      }

      doc.setPage(1);

      const fileName = `Vacation_Requests_Report_${new Date().toISOString().split('T')[0]}.pdf`;
      doc.save(fileName);

      setSnackbarMessage('PDF exported successfully!');
      setSnackbarSeverity('success');
      setSnackbarOpen(true);

    } catch (error) {
      console.error('PDF export failed:', error);
      setExportError(error.message || 'Failed to generate PDF');
      setSnackbarMessage(`PDF export failed: ${error.message || 'Unknown error'}`);
      setSnackbarSeverity('error');
      setSnackbarOpen(true);
    } finally {
      setIsExporting(false);
    }
  };

  const exportExcel = () => {
    try {
      const data = isUrgentView ? [...startingTomorrow, ...startingNextWeek] : getCurrentData();

      if (!data || data.length === 0) {
        setSnackbarMessage('No data available to export');
        setSnackbarSeverity('warning');
        setSnackbarOpen(true);
        return;
      }

      // Apply mapping to Excel as well
      const excelData = data.map(leave => ({
        'Employee': `${leave.employee?.firstName || ''} ${leave.employee?.lastName || ''}`.trim() || 'N/A',
        'Department': leave.employee?.department?.name || 'N/A',
        'Office': leave.employee?.office?.name || leave.employee?.location || 'N/A',
        'Leave Type': formatLeaveType(leave.leaveType || leave.type),
        'Start Date': getFormattedDate(leave.startDate),
        'End Date': getFormattedDate(leave.endDate),
        'Duration': getDuration(leave) || 'N/A',
        'Hours': formatHours(leave),
      }));

      const ws = XLSX.utils.json_to_sheet(excelData);

      const headerStyle = {
        font: { bold: true, color: { rgb: 'FFFFFF' }, sz: 12 },
        fill: { fgColor: { rgb: '1E293B' } },
        alignment: { horizontal: 'center', vertical: 'middle', wrapText: true },
        border: {
          top: { style: 'thin', color: { rgb: 'FFFFFF' } },
          bottom: { style: 'thin', color: { rgb: 'FFFFFF' } },
          left: { style: 'thin', color: { rgb: 'FFFFFF' } },
          right: { style: 'thin', color: { rgb: 'FFFFFF' } }
        }
      };

      const range = XLSX.utils.decode_range(ws['!ref']);
      for (let C = range.s.c; C <= range.e.c; ++C) {
        const cellAddress = { c: C, r: 0 };
        const cellRef = XLSX.utils.encode_cell(cellAddress);
        if (!ws[cellRef]) continue;
        ws[cellRef].s = headerStyle;
      }

      const colWidths = [];
      excelData.forEach(row => {
        Object.entries(row).forEach(([key, value], index) => {
          const cellValue = value?.toString() || '';
          const width = Math.min(30, Math.max(12, cellValue.length + 2));
          if (!colWidths[index] || width > colWidths[index]) {
            colWidths[index] = width;
          }
        });
      });

      ws['!cols'] = colWidths.map(w => ({ wch: w }));

      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, 'Vacation Requests');

      const fileName = `Vacation_Requests_Report_${new Date().toISOString().split('T')[0]}.xlsx`;
      XLSX.writeFile(wb, fileName);

      setSnackbarMessage('Excel file exported successfully!');
      setSnackbarSeverity('success');
      setSnackbarOpen(true);

    } catch (error) {
      console.error('Excel export failed:', error);
      setSnackbarMessage(`Excel export failed: ${error.message || 'Unknown error'}`);
      setSnackbarSeverity('error');
      setSnackbarOpen(true);
    }
  };

  const handleEmployeeSelect = (id) => {
    setSelectedEmployeeId(id);
    setShowEmployeeCard(false);
  };

  const handleStatusCardClick = (status) => {
    const { periodType, periodValue } = dashboardFilters;
    let year, month;
    if (periodType === 'year') {
      year = parseInt(periodValue);
      month = null;
    } else {
      const [yearStr, monthStr] = periodValue.split('-');
      year = parseInt(yearStr, 10);
      month = parseInt(monthStr, 10);
    }

    setFilters(prev => ({
      ...prev,
      status,
      department: '',
      type: '',
      year,
      month,
      office: dashboardFilters.office === 'All' ? '' : dashboardFilters.office,
    }));
    setActiveTab('requests');
  };


  if (loading) {
    return (
      <Box sx={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        minHeight: '60vh',
        p: 4
      }}>
        <CircularProgress size={56} thickness={4} />
      </Box>
    );
  }

  return (
    <Box sx={{
      position: 'relative',
      minHeight: '100vh',
      p: { xs: 2, sm: 3, md: 4 },
      bgcolor: '#F8FAFC'
    }}>
      <Box ref={exportContentRef} sx={{ position: 'absolute', left: '-9999px' }} />

      <Paper elevation={0} sx={{
        mb: 4,
        bgcolor: 'transparent',
        p: { xs: 2, sm: 3 }
      }}>
        <ToggleHeader
          views={leaveViews}
          activeView={activeView}
          onViewChange={(view) => {
            setActiveView(view);
            sessionStorage.setItem('activeRequestView', view);
          }}
          shouldHighlightUrgent={hasUrgentRequests}
          actions={
            <>
              <ExportButtons
                onExportPDF={exportPDF}
                onExportExcel={exportExcel}
                isExporting={isExporting}
                disabled={loading}
              />
              <ElegantAddButton
                onClick={() => navigate('/leave-request')}
                text={"Fill Request"}
                sx={{ ml: 2 }}
              />
            </>
          }
        />
      </Paper>

      {hasUrgentRequests && isUrgentView && (
        <Box sx={{ mb: 3 }}>
          <UrgentLeaveRequests
            setSelectedLeave={setSelectedLeaveId}
            IsEmployee={IsEmployee}
            fetchData={fetchData}
          />
        </Box>
      )}
      {!isUrgentView &&
        < Paper elevation={1} sx={{
          borderRadius: 3,
          overflow: 'hidden',
          bgcolor: 'background.paper',
          p: { xs: 2, sm: 3 },
          boxShadow: '0 1px 3px rgba(0,0,0,0.1)'
        }}>
          <Requests
            leavesData={getCurrentData()}
            filters={filters}
            setFilters={!isUrgentView ? setFilters : () => { }}
            setSelectedLeave={setSelectedLeaveId}
            setLeavesData={setLeavesData}
            IsEmployee={IsEmployee}
            isUrgentView={isUrgentView}
            disableFilters={isUrgentView}
            fetchData={fetchData}
            req={true}
          />
        </Paper>
      }
      {
        selectedLeaveId && (
          <LeaveDetailsModal
            leaveId={selectedLeaveId}
            setSelectedLeave={setSelectedLeaveId}
            setLeavesData={setLeavesData}
            modalRef={modalRef}
            rejectionDialogRef={rejectionDialogRef}
            IsEmployee={IsEmployee}
          />
        )
      }

      <Snackbar
        open={snackbarOpen}
        autoHideDuration={6000}
        onClose={() => setSnackbarOpen(false)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        sx={{
          '& .MuiSnackbarContent-root': {
            borderRadius: 2,
            minWidth: '300px'
          }
        }}
      >
        <Alert
          onClose={() => setSnackbarOpen(false)}
          severity={snackbarSeverity}
          sx={{
            width: '100%',
            fontWeight: 600,
            borderRadius: 2
          }}
          iconMapping={{
            success: <FileText size={24} />,
            error: <AlertTriangle size={24} />,
            warning: <AlertTriangle size={24} />
          }}
        >
          {snackbarMessage}
        </Alert>
      </Snackbar>
    </Box >
  );
};

export default VacationRequests;